import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { doctorService } from '../services/api';

const Doctors = () => {
  const { currentUser, hasModule } = useAuth();
  const { t } = useLanguage();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddDoctorForm, setShowAddDoctorForm] = useState(false);
  const [newDoctor, setNewDoctor] = useState({
    primary_name: '',
    gender: '',
    speciality: '',
    service_provided: '',
    type: 'doctor',
    external_doctor_code: '',
    meta_data: {
      i18n: {
        name: {
          'en_US': '',
          'zh_HK': '',
          'zh_CN': ''
        }
      }
    }
  });
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Check if Clinic Assist module is enabled
  const isClinicAssistEnabled = hasModule('integration_sg_clinic_assist');

  // Gender options
  const genderOptions = [
    { value: 'male', label: t('doctor.male', 'Male') },
    { value: 'female', label: t('doctor.female', 'Female') },
    { value: 'other', label: t('doctor.other', 'Other') }
  ];

  // Type options
  const typeOptions = [
    { value: 'doctor', label: t('doctor.doctor', 'Doctor') },
    { value: 'clinic', label: t('doctor.clinic', 'Clinic') },
    { value: 'facility', label: t('doctor.facility', 'Facility') }
  ];

  // Load doctors when component mounts
  useEffect(() => {
    const loadDoctors = async () => {
      try {
        setLoading(true);
        const data = await doctorService.getDoctors();
        setDoctors(data.items || []);
      } catch (err) {
        console.error('Error loading doctors:', err);
        setError(t('doctor.loadError', 'Failed to load doctors. Please try again later.'));
      } finally {
        setLoading(false);
      }
    };

    loadDoctors();
  }, [t]);

  const handleNewDoctorChange = (e) => {
    const { name, value } = e.target;
    setNewDoctor(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNewDoctorI18nChange = (languageCode, value) => {
    setNewDoctor(prev => ({
      ...prev,
      meta_data: {
        ...prev.meta_data,
        i18n: {
          ...prev.meta_data?.i18n,
          name: {
            ...prev.meta_data?.i18n?.name,
            [languageCode]: value
          }
        }
      }
    }));
  };

  const handleAddDoctor = async () => {
    // Validate required fields
    if (!newDoctor.primary_name || !newDoctor.speciality || !newDoctor.service_provided) {
      setError(t('doctor.fillRequired', 'Please fill in all required fields.'));
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      
      // Build meta_data if any i18n names are provided
      const hasI18nNames = newDoctor.meta_data?.i18n?.name && 
        Object.values(newDoctor.meta_data.i18n.name).some(name => name && name.trim());
      
      const doctorData = { ...newDoctor };
      if (hasI18nNames) {
        doctorData.meta_data = {
          i18n: {
            name: Object.fromEntries(
              Object.entries(newDoctor.meta_data.i18n.name).filter(([_, value]) => value && value.trim())
            )
          }
        };
      } else {
        delete doctorData.meta_data;
      }
      
      const data = await doctorService.addDoctor(doctorData);

      if (data.success) {
        setDoctors(prev => [...prev, data.data]);
        setNewDoctor({
          primary_name: '',
          gender: '',
          speciality: '',
          service_provided: '',
          type: 'doctor',
          external_doctor_code: '',
          meta_data: {
            i18n: {
              name: {
                'en_US': '',
                'zh_HK': '',
                'zh_CN': ''
              }
            }
          }
        });
        setShowAddDoctorForm(false);
        setSuccessMessage(t('doctor.addSuccess', 'Doctor added successfully'));
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(data.message || t('doctor.addError', 'Failed to add doctor'));
      }
    } catch (err) {
      console.error('Error adding doctor:', err);
      setError(t('doctor.addError', 'Failed to add doctor. Please try again later.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditDoctor = (doctor) => {
    setEditingDoctor({
      ...doctor,
      gender: doctor.gender || '',
      type: doctor.type || 'doctor',
      meta_data: {
        i18n: {
          name: {
            'en_US': doctor.meta_data?.i18n?.name?.['en_US'] || '',
            'zh_HK': doctor.meta_data?.i18n?.name?.['zh_HK'] || '',
            'zh_CN': doctor.meta_data?.i18n?.name?.['zh_CN'] || ''
          }
        }
      }
    });
  };

  const handleEditDoctorChange = (e) => {
    const { name, value } = e.target;
    setEditingDoctor(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditDoctorI18nChange = (languageCode, value) => {
    setEditingDoctor(prev => ({
      ...prev,
      meta_data: {
        ...prev.meta_data,
        i18n: {
          ...prev.meta_data?.i18n,
          name: {
            ...prev.meta_data?.i18n?.name,
            [languageCode]: value
          }
        }
      }
    }));
  };

  const handleUpdateDoctor = async () => {
    // Validate required fields
    if (!editingDoctor.primary_name || !editingDoctor.speciality || !editingDoctor.service_provided) {
      setError(t('doctor.fillRequired', 'Please fill in all required fields.'));
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      
      // Build meta_data if any i18n names are provided
      const hasI18nNames = editingDoctor.meta_data?.i18n?.name && 
        Object.values(editingDoctor.meta_data.i18n.name).some(name => name && name.trim());
      
      const updateData = {
        primary_name: editingDoctor.primary_name,
        gender: editingDoctor.gender,
        speciality: editingDoctor.speciality,
        service_provided: editingDoctor.service_provided,
        type: editingDoctor.type,
        external_doctor_code: editingDoctor.external_doctor_code
      };
      
      if (hasI18nNames) {
        updateData.meta_data = {
          i18n: {
            name: Object.fromEntries(
              Object.entries(editingDoctor.meta_data.i18n.name).filter(([_, value]) => value && value.trim())
            )
          }
        };
      }
      
      const data = await doctorService.updateDoctor(editingDoctor.id, updateData);

      if (data.success) {
        setDoctors(prev => prev.map(doc => doc.id === editingDoctor.id ? data.data : doc));
        setEditingDoctor(null);
        setSuccessMessage(t('doctor.updateSuccess', 'Doctor updated successfully'));
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(data.message || t('doctor.updateError', 'Failed to update doctor'));
      }
    } catch (err) {
      console.error('Error updating doctor:', err);
      setError(t('doctor.updateError', 'Failed to update doctor. Please try again later.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDoctor = async (doctorId) => {
    if (!window.confirm(t('doctor.deleteConfirm', 'Are you sure you want to delete this doctor?'))) {
      return;
    }

    try {
      setIsSaving(true);
      const data = await doctorService.deleteDoctor(doctorId);

      if (data.success) {
        setDoctors(prev => prev.filter(doc => doc.id !== doctorId));
        setSuccessMessage(t('doctor.deleteSuccess', 'Doctor deleted successfully'));
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(data.message || t('doctor.deleteError', 'Failed to delete doctor'));
      }
    } catch (err) {
      console.error('Error deleting doctor:', err);
      setError(t('doctor.deleteError', 'Failed to delete doctor. Please try again later.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingDoctor(null);
    setError(null);
  };

  if (loading && doctors.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#075e54]"></div>
        <p className="ml-4 text-gray-600">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('doctor.title')}</h1>
        <button
          onClick={() => setShowAddDoctorForm(!showAddDoctorForm)}
          className="px-4 py-2 bg-[#075e54] text-white rounded hover:bg-[#064e45] transition-colors"
          disabled={isSaving}
        >
          {showAddDoctorForm ? t('common.cancel') : t('doctor.add')}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700">
          {error}
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700">
          {successMessage}
        </div>
      )}

      {/* Add Doctor Form */}
      {showAddDoctorForm && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded">
          <h2 className="text-lg font-semibold mb-4">{t('doctor.add')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('doctor.name')} *
              </label>
              <input
                type="text"
                name="primary_name"
                value={newDoctor.primary_name}
                onChange={handleNewDoctorChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54]"
                placeholder={t('doctor.enterName', 'Enter doctor name')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('doctor.gender', 'Gender')}
              </label>
              <select
                name="gender"
                value={newDoctor.gender}
                onChange={handleNewDoctorChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54]"
              >
                <option value="">{t('doctor.selectGender', 'Select gender')}</option>
                {genderOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('doctor.specialization')} *
              </label>
              <input
                type="text"
                name="speciality"
                value={newDoctor.speciality}
                onChange={handleNewDoctorChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54]"
                placeholder={t('doctor.enterSpecialization', 'Enter specialization')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('doctor.serviceProvided', 'Service Provided')} *
              </label>
              <input
                type="text"
                name="service_provided"
                value={newDoctor.service_provided}
                onChange={handleNewDoctorChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54]"
                placeholder={t('doctor.enterService', 'Enter service provided')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('doctor.type', 'Type')}
              </label>
              <select
                name="type"
                value={newDoctor.type}
                onChange={handleNewDoctorChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54]"
              >
                {typeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {isClinicAssistEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('doctor.externalDoctorCode', 'External Doctor Code')}
                </label>
                <input
                  type="text"
                  name="external_doctor_code"
                  value={newDoctor.external_doctor_code}
                  onChange={handleNewDoctorChange}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54]"
                  placeholder={t('doctor.enterExternalCode', 'Enter external doctor code')}
                />
              </div>
            )}
          </div>
          
          {/* i18n Name Translations */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('doctor.nameTranslations', 'Name Translations (Optional)')}
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">English (en_US)</label>
                <input
                  type="text"
                  value={newDoctor.meta_data?.i18n?.name?.['en_US'] || ''}
                  onChange={(e) => handleNewDoctorI18nChange('en_US', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#075e54]"
                  placeholder="English name"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">繁體中文 (zh_HK)</label>
                <input
                  type="text"
                  value={newDoctor.meta_data?.i18n?.name?.['zh_HK'] || ''}
                  onChange={(e) => handleNewDoctorI18nChange('zh_HK', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#075e54]"
                  placeholder="繁體中文名稱"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">简体中文 (zh_CN)</label>
                <input
                  type="text"
                  value={newDoctor.meta_data?.i18n?.name?.['zh_CN'] || ''}
                  onChange={(e) => handleNewDoctorI18nChange('zh_CN', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#075e54]"
                  placeholder="简体中文名称"
                />
              </div>
            </div>
          </div>
          
          <div className="mt-4 flex space-x-2">
            <button
              onClick={handleAddDoctor}
              disabled={isSaving}
              className="px-4 py-2 bg-[#075e54] text-white rounded hover:bg-[#064e45] disabled:bg-gray-400 transition-colors"
            >
              {isSaving ? t('doctor.saving', 'Saving...') : t('doctor.save')}
            </button>
            <button
              onClick={() => setShowAddDoctorForm(false)}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Doctors List */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white">
          <thead>
            <tr className="bg-gray-100">
              <th className="py-2 px-4 border-b text-left font-medium text-gray-700">ID</th>
              <th className="py-2 px-4 border-b text-left font-medium text-gray-700">{t('doctor.name')}</th>
              <th className="py-2 px-4 border-b text-left font-medium text-gray-700">{t('doctor.gender', 'Gender')}</th>
              <th className="py-2 px-4 border-b text-left font-medium text-gray-700">{t('doctor.specialization')}</th>
              <th className="py-2 px-4 border-b text-left font-medium text-gray-700">{t('doctor.serviceProvided', 'Service Provided')}</th>
              <th className="py-2 px-4 border-b text-left font-medium text-gray-700">{t('doctor.type', 'Type')}</th>
              {isClinicAssistEnabled && (
                <th className="py-2 px-4 border-b text-left font-medium text-gray-700">{t('doctor.externalDoctorCode', 'External Code')}</th>
              )}
              <th className="py-2 px-4 border-b text-left font-medium text-gray-700">{t('common.actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody>
            {doctors.length === 0 ? (
              <tr>
                <td colSpan={isClinicAssistEnabled ? "8" : "7"} className="py-8 text-center text-gray-500">
                  {t('doctor.noData', 'No doctors found. Add your first doctor using the button above.')}
                </td>
              </tr>
            ) : (
              doctors.map((doctor) => (
                <React.Fragment key={doctor.id}>
                  <tr className="hover:bg-gray-50">
                    {editingDoctor && editingDoctor.id === doctor.id ? (
                      <>
                        <td className="py-2 px-4 border-b font-mono text-sm text-gray-600">
                          {doctor.id}
                        </td>
                        <td className="py-2 px-4 border-b">
                          <input
                            type="text"
                            name="primary_name"
                            value={editingDoctor.primary_name}
                            onChange={handleEditDoctorChange}
                            className="w-full p-1 border border-gray-300 rounded"
                          />
                        </td>
                        <td className="py-2 px-4 border-b">
                          <select
                            name="gender"
                            value={editingDoctor.gender}
                            onChange={handleEditDoctorChange}
                            className="w-full p-1 border border-gray-300 rounded"
                          >
                            <option value="">{t('doctor.selectGender', 'Select gender')}</option>
                            {genderOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 px-4 border-b">
                          <input
                            type="text"
                            name="speciality"
                            value={editingDoctor.speciality}
                            onChange={handleEditDoctorChange}
                            className="w-full p-1 border border-gray-300 rounded"
                          />
                        </td>
                        <td className="py-2 px-4 border-b">
                          <input
                            type="text"
                            name="service_provided"
                            value={editingDoctor.service_provided}
                            onChange={handleEditDoctorChange}
                            className="w-full p-1 border border-gray-300 rounded"
                          />
                        </td>
                        <td className="py-2 px-4 border-b">
                          <select
                            name="type"
                            value={editingDoctor.type}
                            onChange={handleEditDoctorChange}
                            className="w-full p-1 border border-gray-300 rounded"
                          >
                            {typeOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        {isClinicAssistEnabled && (
                          <td className="py-2 px-4 border-b">
                            <input
                              type="text"
                              name="external_doctor_code"
                              value={editingDoctor.external_doctor_code}
                              onChange={handleEditDoctorChange}
                              className="w-full p-1 border border-gray-300 rounded"
                            />
                          </td>
                        )}
                        <td className="py-2 px-4 border-b">
                          <div className="flex space-x-2">
                            <button
                              onClick={handleUpdateDoctor}
                              disabled={isSaving}
                              className="px-2 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 disabled:bg-gray-400"
                            >
                              {isSaving ? t('doctor.saving', 'Saving...') : t('doctor.save')}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                            >
                              {t('common.cancel')}
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-2 px-4 border-b font-mono text-sm text-gray-600">{doctor.id}</td>
                        <td className="py-2 px-4 border-b">{doctor.primary_name}</td>
                        <td className="py-2 px-4 border-b">
                          {doctor.gender ? genderOptions.find(g => g.value === doctor.gender)?.label || doctor.gender : '-'}
                        </td>
                        <td className="py-2 px-4 border-b">{doctor.speciality}</td>
                        <td className="py-2 px-4 border-b">{doctor.service_provided}</td>
                        <td className="py-2 px-4 border-b">
                          {typeOptions.find(t => t.value === doctor.type)?.label || doctor.type}
                        </td>
                        {isClinicAssistEnabled && (
                          <td className="py-2 px-4 border-b">
                            {doctor.external_doctor_code || '-'}
                          </td>
                        )}
                        <td className="py-2 px-4 border-b">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditDoctor(doctor)}
                              className="px-2 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                              disabled={isSaving}
                            >
                              {t('doctor.edit')}
                            </button>
                            <button
                              onClick={() => handleDeleteDoctor(doctor.id)}
                              className="px-2 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                              disabled={isSaving}
                            >
                              {t('doctor.delete')}
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                  {editingDoctor && editingDoctor.id === doctor.id && (
                    <tr>
                      <td colSpan={isClinicAssistEnabled ? "8" : "7"} className="py-2 px-4 border-b">
                        <div className="p-2 bg-blue-50 border border-blue-200 rounded">
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            {t('doctor.nameTranslations', 'Name Translations (Optional)')}
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <div>
                              <label className="block text-xs text-gray-600 mb-0.5">English (en_US)</label>
                              <input
                                type="text"
                                value={editingDoctor.meta_data?.i18n?.name?.['en_US'] || ''}
                                onChange={(e) => handleEditDoctorI18nChange('en_US', e.target.value)}
                                className="w-full p-1 border border-gray-300 rounded text-xs"
                                placeholder="English name"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-0.5">繁體中文 (zh_HK)</label>
                              <input
                                type="text"
                                value={editingDoctor.meta_data?.i18n?.name?.['zh_HK'] || ''}
                                onChange={(e) => handleEditDoctorI18nChange('zh_HK', e.target.value)}
                                className="w-full p-1 border border-gray-300 rounded text-xs"
                                placeholder="繁體中文名稱"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-0.5">简体中文 (zh_CN)</label>
                              <input
                                type="text"
                                value={editingDoctor.meta_data?.i18n?.name?.['zh_CN'] || ''}
                                onChange={(e) => handleEditDoctorI18nChange('zh_CN', e.target.value)}
                                className="w-full p-1 border border-gray-300 rounded text-xs"
                                placeholder="简体中文名称"
                              />
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Doctors;
